<template>
  <div class="shared-link-manage-dialog-box">

    <el-dialog
      width-size="medium"
      :title="$t('wrh-dashboard.shared-link-dialog-box.manage-view.title')"
      @close="onClose()"
      :show-close="false"
      visible>

      <div class="mx-24 my-24">

        <el-row>
          <el-col :span="14">
            <span class="el-text el-text--subheading">
              {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.caption.password-protection') }}
            </span>
          </el-col>
          <el-col
            :span="10"
            style="text-align: right;">
            <el-switch v-model="isPasswordProtectionEnabled"/>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="24">
            <span
              class="el-text el-text--body-24"
              :class="{ 'el-text--color-fixed-light': !isPasswordProtectionEnabled}"
            >
              {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.message.protect-by-password') }}
            </span>
          </el-col>
        </el-row>
        <el-row class="mt-16">
          <el-col :span="24">
            <el-password
              @focus="onPasswordInputFocus"
              style="max-width: 304px;"
              :disabled="!isPasswordProtectionEnabled || isResetPasswordVisible"
              v-model="linkPassword"
              :label="$t('wrh-dashboard.shared-link-dialog-box.manage-view.input.hint.password')"
              :with-icon="!isResetPasswordVisible"
            />
          </el-col>

          <el-button
            style="margin-top: 4px;"
            v-if="isResetPasswordVisible"
            :disabled="!isPasswordProtectionEnabled"
            @click="onPasswordReset"
            type="text">
            {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.button.reset-password') }}
          </el-button>

        </el-row>

        <el-divider class="mt-24 mb-24"/>

        <el-row>
          <el-col :span="14">
            <span class="el-text el-text--subheading">
              {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.caption.expiration') }}
            </span>
          </el-col>
          <el-col
            :span="10"
            style="text-align: right;">
            <el-switch v-model="isLinkExpirationEnabled"/>
          </el-col>
        </el-row>
        <el-row style="margin-top: 4px;">
          <el-col :span="24">
            <span
              class="el-text el-text--body-24"
              :class="{ 'el-text--color-fixed-light': !isLinkExpirationEnabled}"
              style="margin-right: 4px;">
              {{ $tc('wrh-dashboard.shared-link-dialog-box.manage-view.label.disable-in:plural',
                     linkTTLDays, {splitMarker: splitMarker}).split(splitMarker)[0] }}
            </span>

            <el-num-picker
              :disabled="!isLinkExpirationEnabled"
              v-model="linkTTLDays"
              style="max-width: 112px;"
              :min="1"
              :max="999"
              :debounce="1000"
              class="mr-16"
              integer-only
            />

            <span
              class="el-text el-text--body-24"
              :class="{ 'el-text--color-fixed-light': !isLinkExpirationEnabled}"
              style="margin-left: -12px;">
              {{ $tc('wrh-dashboard.shared-link-dialog-box.manage-view.label.disable-in:plural',
                     linkTTLDays, {splitMarker: splitMarker}).split(splitMarker)[1] }}
            </span>
          </el-col>
        </el-row>

        <el-divider class="mt-24 mb-24"/>

        <el-row>
          <el-col :span="14">
            <span class="el-text el-text--subheading">
              {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.caption.download-limits') }}
            </span>
          </el-col>
          <el-col
            :span="10"
            style="text-align: right;">
            <el-switch v-model="isLinkDownloadsLimitEnabled"/>
          </el-col>
        </el-row>
        <el-row style="margin-top: 4px;">
          <el-col :span="24">
            <span
              class="el-text el-text--body-24"
              :class="{ 'el-text--color-fixed-light': !isLinkDownloadsLimitEnabled}"
              style="margin-right: 4px;">
              {{ $tc('wrh-dashboard.shared-link-dialog-box.manage-view.label.disable-after:plural',
                     linkMaxDownloads, {splitMarker: splitMarker}).split(splitMarker)[0] }}
            </span>

            <el-num-picker
              :disabled="!isLinkDownloadsLimitEnabled"
              v-model="linkMaxDownloads"
              style="max-width: 112px;"
              :min="1"
              :max="999"
              :debounce="1000"
              class="mr-16"
              integer-only
            />

            <span
              class="el-text el-text--body-24"
              :class="{ 'el-text--color-fixed-light': !isLinkDownloadsLimitEnabled}"
              style="margin-left: -12px;">
              {{ $tc('wrh-dashboard.shared-link-dialog-box.manage-view.label.disable-after:plural',
                     linkMaxDownloads, {splitMarker: splitMarker}).split(splitMarker)[1] }}
            </span>
          </el-col>
        </el-row>

      </div>

      <template #footer-aside>
        <el-button
          icon="i-trash-o--16"
          type="text"
          @click="onDelete()"
        >
          {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.button.delete') }}
        </el-button>
      </template>
      <template #footer>
        <el-button
          @click="onCancel()"
          native-type="button"
          type="secondary">
          {{ $t('wrh-dashboard.form.button.cancel') }}
        </el-button>

        <el-button
          native-type="button"
          @click="onApplySettings()"
          :loading="isUpdateSharedLinkInProgress"
          :disabled="isUpdateSharedLinkInProgress || !isApplyButtonEnabled"
        >
          {{ $t('wrh-dashboard.shared-link-dialog-box.manage-view.button.apply') }}
        </el-button>

      </template>
    </el-dialog>

  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import {
  FETCH_SHARED_LINK_SETTINGS,
  UPDATE_SHARED_LINK_SETTINGS
} from '../store/action-types';

import {
  PENDING_STATUS,
  COMPLETED_STATUS
} from '../store/action-status';

const DEFAULT_LINK_TTL_DAYS = 14;
const DEFAULT_LINK_MAX_DOWNLOADS = 10;
const DEFAULT_NOT_EMPTY_PASSWORD_VALUE = '********';

const { mapGetters, mapActions } = createNamespacedHelpers('wrh-dashboard');

export default {
  name: 'SharedLinkManageDialogBox',
  props: {
    linkId: {
      type: Number,
      default: 0,
      required: true
    }
  },
  data() {
    return {
      isPasswordProtectionEnabled: false,
      linkPassword: '',
      isLinkExpirationEnabled: false,
      linkTTLDays: DEFAULT_LINK_TTL_DAYS,
      isLinkDownloadsLimitEnabled: false,
      linkMaxDownloads: DEFAULT_LINK_MAX_DOWNLOADS,
      splitMarker: '!@!',
      isResetPasswordVisible: false
    };
  },
  computed: {
    ...mapGetters([
      'getSharedLinkSettings',
      'getFetchSharedLinkStatus',
      'getUpdateSharedLinkStatus'
    ]),
    isApplyButtonEnabled() {
      if (this.isPasswordProtectionEnabled &&
        !this.isResetPasswordVisible &&
        this.linkPassword === ''
      ) {
        return false;
      }
      return true;
    },
    isFetchSharedLinkSettingsCompleted() {
      return this.getFetchSharedLinkStatus === COMPLETED_STATUS;
    },
    isUpdateSharedLinkInProgress() {
      return this.getUpdateSharedLinkStatus === PENDING_STATUS;
    },
    isUpdateSharedLinkCompleted() {
      return this.getUpdateSharedLinkStatus === COMPLETED_STATUS;
    }
  },
  watch: {
    isFetchSharedLinkSettingsCompleted() {
      if (this.getSharedLinkSettings.passwordProtected) {
        this.isPasswordProtectionEnabled = true;
        this.linkPassword = DEFAULT_NOT_EMPTY_PASSWORD_VALUE;
        this.isResetPasswordVisible = true;
      } else {
        this.isPasswordProtectionEnabled = false;
        this.linkPassword = '';
        this.isResetPasswordVisible = false;
      }

      const linkTtl = this.getSharedLinkSettings.linkTtlDays;
      if (linkTtl > 0) {
        this.isLinkExpirationEnabled = true;
        this.linkTTLDays = linkTtl;
      } else {
        this.isLinkExpirationEnabled = false;
        this.linkTTLDays = DEFAULT_LINK_TTL_DAYS;
      }

      const linkMaxDownloads = this.getSharedLinkSettings.linkMaxDownloads;
      if (linkMaxDownloads > 0) {
        this.isLinkDownloadsLimitEnabled = true;
        this.linkMaxDownloads = linkMaxDownloads;
      } else {
        this.isLinkDownloadsLimitEnabled = false;
        this.linkMaxDownloads = DEFAULT_LINK_MAX_DOWNLOADS;
      }
    },
    isUpdateSharedLinkCompleted() {
      this.onClose();
    }
  },
  methods: {
    ...mapActions({
      fetchSharedLinkSettings: FETCH_SHARED_LINK_SETTINGS,
      updateSharedLinkSettings: UPDATE_SHARED_LINK_SETTINGS
    }),
    onPasswordReset() {
      this.isResetPasswordVisible = false;
      this.linkPassword = '';
    },
    onPasswordInputFocus() {
      if (this.linkPassword === DEFAULT_NOT_EMPTY_PASSWORD_VALUE) {
        this.linkPassword = '';
      }
    },
    onApplySettings() {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WR_sharing',
        eventAction: 'Link_settings_pass',
        eventLabel: this.isPasswordProtectionEnabled ? '1' : '0'
      });

      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WR_sharing',
        eventAction: 'Link_settings_expiration',
        eventLabel: this.isLinkExpirationEnabled ? '1' : '0'
      });

      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WR_sharing',
        eventAction: 'Link_settings_limit',
        eventLabel: this.isLinkDownloadsLimitEnabled ? '1' : '0'
      });

      this.updateSharedLinkSettings({
        linkId: this.linkId,
        passwordProtected: this.isPasswordProtectionEnabled,
        linkPassword: this.linkPassword !== DEFAULT_NOT_EMPTY_PASSWORD_VALUE ? this.linkPassword : '',
        linkTtlDays: this.isLinkExpirationEnabled ? this.linkTTLDays : 0,
        linkMaxDownloads: this.isLinkDownloadsLimitEnabled ? this.linkMaxDownloads : 0
      });
    },
    loadLinkSettings() {
      this.isResetPasswordVisible = false;
      this.fetchSharedLinkSettings({
        linkId: this.linkId
      });
    },
    onDelete() {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WR_sharing',
        eventAction: 'Link_settings_delete',
        eventLabel: ''
      });

      this.$emit('handleDelete', this.linkId);
    },
    onCancel() {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: 'WR_sharing',
        eventAction: 'Link_settings_cancel',
        eventLabel: ''
      });

      this.onClose();
    },
    onClose() {
      this.$emit('handleClose');
      this.isPasswordProtectionEnabled = false;
      this.linkPassword = '';
      this.isLinkExpirationEnabled = false;
      this.linkTTLDays = DEFAULT_LINK_TTL_DAYS;
      this.isLinkDownloadsLimitEnabled = false;
      this.linkMaxDownloads = DEFAULT_LINK_MAX_DOWNLOADS;
    }
  },
  mounted() {
    this.loadLinkSettings();
  }
};
</script>

<style lang="scss" scoped>

</style>
